import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MetricType, TeamMetricResult } from '../../models/team-metrics';

@Component({
  selector: 'app-metric-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './metric-card.component.html',
  styleUrls: ['./metric-card.component.css']
})
export class MetricCardComponent implements OnChanges {
  @Input() title: string = '';
  @Input() icon: string = '';
  @Input() metricData: TeamMetricResult | null = null;
  @Input() isLoading: boolean = false;
  @Input() tooltipText: string = '';
  @Input() metricType: MetricType = 'genericPositiveIsGood';

  currentAnimatedValue: string | null = '';
  private animationFrameId: number | null = null;

  constructor() { }

  /**
   * Angular lifecycle hook that responds when Angular sets or resets data-bound input properties.
   * Triggers value animation if `metricData` changes.
   * @param changes Object containing the changed properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metricData']) {
      if (!this.metricData || this.metricData.displayValue === 'N/A' || typeof this.metricData.value !== 'number') {
        this.currentAnimatedValue = this.metricData?.displayValue ?? (this.isLoading ? '' : 'N/A');
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        return;
      }
      this.animateValue(this.metricData);
    }
  }

  /**
   * Animates the metric value from a start point to the targetData.value.
   * It uses requestAnimationFrame for smooth animation and formats the
   * display string based on the metricType.
   * @param targetData The target metric data to animate to.
   */
  animateValue(targetData: TeamMetricResult): void {
    if (this.metricType === 'pbiTimeByColumn') {
      this.currentAnimatedValue = targetData.displayValue;
      return;
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    const startValue = parseFloat(this.currentAnimatedValue?.match(/[\d.]+/)?.[0] ?? '0') || 0;
    const targetValue = targetData.value;
    
    if (startValue === targetValue && this.currentAnimatedValue === targetData.displayValue) {
        this.currentAnimatedValue = targetData.displayValue;
        return;
    }

    const duration = 300;
    let startTime: number | null = null;

    const decimalPlaces = (this.metricType === 'deploymentFrequency') ? 0 : 1;

    const animationStep = (timestamp: number) => {
      startTime ??= timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const currentValue = startValue + (targetValue - startValue) * progress;

      if (this.metricType === 'prTime') {
        if (progress === 1) {
          this.currentAnimatedValue = targetData.displayValue;
        } else {
          this.currentAnimatedValue = `${currentValue.toFixed(decimalPlaces)} hours`;
        }
      } else {
        let animatedString = '';
        switch (this.metricType) {
          case 'deploymentFrequency': {
            const valToShow = currentValue.toFixed(decimalPlaces);
            animatedString = `${valToShow} release${valToShow === '1' ? '' : 's'}`;
            break;
          }
          case 'changeFailRate':
            animatedString = `${currentValue.toFixed(decimalPlaces)}%`;
            break;
          default:
            animatedString = currentValue.toFixed(decimalPlaces);
        }
        this.currentAnimatedValue = animatedString;
      }
      

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animationStep);
      } else {
        this.currentAnimatedValue = targetData.displayValue;
        this.animationFrameId = null;
      }
    };
    this.animationFrameId = requestAnimationFrame(animationStep);
  }

  /**
   * Calculates the display properties (text, CSS class, icon) for the
   * period-over-period comparison indicator.
   * @returns An object with `text`, `class`, and `icon` for the comparison display.
   */
  getComparisonDisplay(): { text: string, class: string, icon: string } {
    if (!this.metricData || typeof this.metricData.percentageChange !== 'number' || !this.metricData.comparisonPeriod) {
      return { text: '', class: '', icon: '' };
    }

    const abs = Math.abs(this.metricData.percentageChange);
    const direction = this.metricData.percentageChange > 0 ? '↑' : '↓';

    let isImprovement: boolean;
    switch (this.metricType) {
      case 'deploymentFrequency':
        isImprovement = this.metricData.percentageChange > 0; // Higher is better
        break;
      case 'changeFailRate':
      case 'prTime':
        isImprovement = this.metricData.percentageChange < 0; // Lower is better
        break;
      case 'genericPositiveIsGood':
        isImprovement = this.metricData.percentageChange > 0;
        break;
      case 'genericNegativeIsGood':
        isImprovement = this.metricData.percentageChange < 0;
        break;
      default:
        isImprovement = false;
    }

    return {
      text: `${direction} ${abs.toFixed(0)}% ${this.metricData.comparisonPeriod}`,
      class: isImprovement ? 'comparison-good' : 'comparison-bad',
      icon: direction
    };
  }
}
